import { parseDateSafely, getDurationInDays } from './dateUtils';

// Cache for parsed sections JSON to avoid repeated parsing
const sectionsCache = new Map();

// Cache for calculation results to avoid recalculating
const calculationCache = new Map();
const CACHE_MAX_SIZE = 1000; // Limit cache size to prevent memory issues

function getCacheKey(proposal, type) {
  const id = proposal.id || proposal.projectNumber || '';
  const version = proposal.version || 1;
  const sectionsHash = proposal.sectionsJSON?.substring(0, 20) || '';
  return `${type}_${id}_v${version}_${sectionsHash}`;
}

export const DEFAULT_PC_PCT = 10;
export const DEFAULT_SF_PCT = 5;
export const TAX_RATE = 0.0975;

/**
 * Sales tax that mirrors what QuickBooks actually charges.
 *
 * QuickBooks records the proposal discount as a single DiscountLineDetail and applies
 * it proportionally across EVERY invoice line, including the non-taxable credit card
 * processing fee. The portion of the discount that lands on the CC fee does not reduce
 * the taxable base, so QB ends up taxing more than our net (already-discounted) subtotal.
 *
 * To match QB to the penny we add that "lost" slice of the discount back onto the taxable
 * base before applying the rate:
 *   taxableLinesPreDiscount = subtotal + discount        (taxable lines before discount)
 *   discountOnCcFee         = discount * ccFee / (taxableLinesPreDiscount + ccFee)
 *   taxableBase             = subtotal + discountOnCcFee
 *
 * When there is no CC fee (waived) or no discount, this is identical to the straight
 * `subtotal * rate`, so non-card / non-discounted proposals are unaffected.
 *
 * @param {Object} args
 * @param {number} args.subtotal - Net taxable subtotal (after the full discount).
 * @param {number} args.standardRateDiscount - Discount amount (positive).
 * @param {number} args.creditCardFee - Non-taxable CC fee line amount (0 if waived).
 * @param {boolean} args.taxExempt - When true, tax is 0.
 * @returns {number} Tax amount.
 */
export function computeQuickBooksMirroredTax({ subtotal, standardRateDiscount, creditCardFee, taxExempt }) {
  if (taxExempt) return 0;
  const discount = Math.abs(parseFloat(standardRateDiscount) || 0);
  const ccFee = parseFloat(creditCardFee) || 0;
  if (ccFee > 0 && discount > 0) {
    const taxableLinesPreDiscount = subtotal + discount;
    const discountOnCcFee = discount * (ccFee / (taxableLinesPreDiscount + ccFee));
    return (subtotal + discountOnCcFee) * TAX_RATE;
  }
  return subtotal * TAX_RATE;
}

export function resolveProductCareDollarNumber(proposal) {
  const raw = proposal?.productCareDollars;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const m = (proposal?.discountName || '').match(/PCUSD:([\d.]+)/);
  if (m) {
    const n = parseFloat(m[1]);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
}

export function resolveServiceFeeDollarNumber(proposal) {
  const raw = proposal?.serviceFeeDollars;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const m = (proposal?.discountName || '').match(/SFUSD:([\d.]+)/);
  if (m) {
    const n = parseFloat(m[1]);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
}

export function resolveProductCarePercentNumber(proposal) {
  if (resolveProductCareDollarNumber(proposal) !== null) return null;
  const raw = proposal?.productCarePercent;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const m = (proposal?.discountName || '').match(/PCPCT:([\d.]+)/);
  if (m) {
    const n = parseFloat(m[1]);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
}

export function resolveServiceFeePercentNumber(proposal) {
  if (resolveServiceFeeDollarNumber(proposal) !== null) return null;
  const raw = proposal?.serviceFeePercent;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseFloat(String(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const m = (proposal?.discountName || '').match(/SFPCT:([\d.]+)/);
  if (m) {
    const n = parseFloat(m[1]);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return null;
}

export function calculateTotal(proposal) {
  // PERFORMANCE: Check cache first
  const cacheKey = getCacheKey(proposal, 'total');
  if (calculationCache.has(cacheKey)) {
    return calculationCache.get(cacheKey);
  }
  
  let result;
  // If this is a historical project, use the pre-calculated invoice total
  // Check both isHistorical flag and project number starting with HIST-
  const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
  
  if (isHistorical && (proposal.historicalInvoiceTotal !== null && proposal.historicalInvoiceTotal !== undefined)) {
    result = parseFloat(proposal.historicalInvoiceTotal) || 0;
    // Cache historical result
    if (calculationCache.size >= CACHE_MAX_SIZE) {
      const firstKey = calculationCache.keys().next().value;
      calculationCache.delete(firstKey);
    }
    calculationCache.set(cacheKey, result);
    return result;
  }
  
  // PERFORMANCE: Cache parsed sections JSON to avoid repeated parsing
  let sections;
  const sectionsCacheKey = proposal.id || proposal.projectNumber || proposal.sectionsJSON?.substring(0, 50);
  if (sectionsCache.has(sectionsCacheKey)) {
    sections = sectionsCache.get(sectionsCacheKey);
  } else {
    sections = JSON.parse(proposal.sectionsJSON || '[]');
    sectionsCache.set(sectionsCacheKey, sections);
  }
  
  let baseProductTotal = 0;
  sections.forEach(section => {
    if (section.products && Array.isArray(section.products)) {
      section.products.forEach(product => {
        const quantity = parseFloat(product.quantity) || 0;
        const price = parseFloat(product.price) || 0;
        baseProductTotal += quantity * price;
      });
    }
  });
  
  // Get rental multiplier
  let rentalMultiplier = 1.0;
  if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
    const parsed = parseFloat(proposal.customRentalMultiplier);
    if (!isNaN(parsed) && parsed > 0) {
      rentalMultiplier = parsed;
    }
  } else {
    // Duration: delivery→strike 24-hour periods, or date-only floor (no +1) — matches Backend
    const durationInDays = getDurationInDays(proposal);
    rentalMultiplier = getRentalMultiplier(durationInDays);
  }
  
  const extendedProductTotal = baseProductTotal * rentalMultiplier;
  
  // Percentage discount is on BASE product total, not extended (matches Backend)
  const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
  let discountType = 'percentage';
  if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
    const match = proposal.discountName.match(/^TYPE:(\w+)/);
    if (match) discountType = match[1];
  }
  const standardRateDiscount = Math.abs(discountType === 'dollar'
    ? discountValue
    : baseProductTotal * (Math.abs(discountValue) / 100));
  
  const rentalTotal = extendedProductTotal - standardRateDiscount;
  
  let waiveProductCare = proposal.waiveProductCare === true || proposal.waiveProductCare === 'true' || String(proposal.waiveProductCare || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:PC')) waiveProductCare = true;
  let waiveServiceFee = proposal.waiveServiceFee === true || proposal.waiveServiceFee === 'true' || String(proposal.waiveServiceFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:SF')) waiveServiceFee = true;

  const pcUsd = resolveProductCareDollarNumber(proposal);
  const sfUsd = resolveServiceFeeDollarNumber(proposal);
  const pcPct = resolveProductCarePercentNumber(proposal);
  const sfPct = resolveServiceFeePercentNumber(proposal);
  const pcPctEff = pcUsd !== null ? null : (pcPct !== null ? pcPct : DEFAULT_PC_PCT);
  const sfPctEff = sfUsd !== null ? null : (sfPct !== null ? sfPct : DEFAULT_SF_PCT);

  let productCareAmount;
  if (pcUsd !== null) productCareAmount = pcUsd;
  else productCareAmount = extendedProductTotal * ((pcPctEff ?? DEFAULT_PC_PCT) / 100);
  const productCare = waiveProductCare ? 0 : productCareAmount;

  const delivery = parseFloat(proposal.deliveryFee) || 0;

  let serviceFeeAmount;
  if (sfUsd !== null) serviceFeeAmount = sfUsd;
  else serviceFeeAmount = (extendedProductTotal + productCare + delivery) * ((sfPctEff ?? DEFAULT_SF_PCT) / 100);
  const serviceFee = waiveServiceFee ? 0 : serviceFeeAmount;
  
  // Subtotal includes all fees (matches Backend — sum all misc fee amounts)
  let miscFeesTotal = 0;
  try {
    const miscFees = typeof proposal.miscFees === 'string' ? JSON.parse(proposal.miscFees || '[]') : (proposal.miscFees || []);
    if (Array.isArray(miscFees)) {
      miscFees.forEach(fee => {
        miscFeesTotal += parseFloat(fee.amount || 0) || 0;
      });
    }
  } catch (e) {}
  const subtotal = rentalTotal + productCare + serviceFee + delivery + miscFeesTotal;

  const taxExempt = proposal.taxExempt === true || proposal.taxExempt === 'true';

  // Determine whether the 3% credit card processing fee applies
  let waiveCreditCardFee = false;
  if (proposal.discountName && proposal.discountName.includes('WAIVE:CC')) {
    waiveCreditCardFee = true;
  }
  if (proposal.waiveCreditCardFee === true || proposal.waiveCreditCardFee === 'true') {
    waiveCreditCardFee = true;
  }
  if (!waiveCreditCardFee && (proposal.waiveCreditCardFeeFromColumn === true || proposal.waiveCreditCardFeeFromColumn === 'true' || String(proposal.waiveCreditCardFeeFromColumn || '').toLowerCase() === 'true')) {
    waiveCreditCardFee = true;
  }

  // Credit card fee: 3% of (discounted subtotal + straight tax on that subtotal).
  // This is the fixed, NON-taxable line that also appears on the QuickBooks invoice,
  // so it is intentionally based on the straight tax (not the QB-mirrored tax below).
  const straightTax = taxExempt ? 0 : subtotal * 0.0975;
  const creditCardFee = waiveCreditCardFee ? 0 : (subtotal + straightTax) * 0.03;

  // Tax mirrors QuickBooks. In QB the proposal discount is one DiscountLineDetail that
  // QB applies proportionally across ALL invoice lines — including the non-taxable
  // credit card fee. The slice of the discount allocated to the CC fee never reduces
  // the taxable base, so QB taxes a higher base than our net subtotal. Reproduce that
  // here so the proposal total equals the QuickBooks invoice the client is billed.
  const tax = computeQuickBooksMirroredTax({
    subtotal,
    standardRateDiscount,
    creditCardFee,
    taxExempt,
  });

  const total = subtotal + tax + creditCardFee;
  
  result = total;
  
  // PERFORMANCE: Cache result (limit cache size)
  if (calculationCache.size >= CACHE_MAX_SIZE) {
    const firstKey = calculationCache.keys().next().value;
    calculationCache.delete(firstKey);
  }
  calculationCache.set(cacheKey, result);
  
  return result;
}

export function formatNumber(num) {
  return (parseFloat(num) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------
// Client Upgrade (complimentary) — client-facing presentation
// ---------------------------------------------------------------------------
// Mirrors Admin Dashboard/app/lib/clientUpgrade.js. A complimentary line is
// priced at $0 for the client (product.price === 0) but must still display its
// real standard/preferred rate and a distinct "Complimentary Upgrades" totals
// line. The authoritative Rental Total is NOT changed here — complimentary lines
// already contribute $0. These helpers only reconstruct the display breakdown.

export function isComplimentaryUpgradeActive(product) {
  const u = product && product.clientUpgrade;
  return !!(u && typeof u === 'object' && u.active === true);
}

/** Per-row display for a complimentary line (real rates, $0 total). */
export function getComplimentaryRowDisplay(product) {
  if (!isComplimentaryUpgradeActive(product)) return { isComplimentary: false };
  const u = product.clientUpgrade;
  const standardUnitPrice = parseFloat(u.standardUnitPrice) || 0;
  const discountedUnitPrice =
    u.discountedUnitPrice === undefined || u.discountedUnitPrice === null
      ? standardUnitPrice
      : parseFloat(u.discountedUnitPrice) || 0;
  return {
    isComplimentary: true,
    label: 'Complimentary Upgrade',
    standardUnitPrice,
    preferredUnitPrice: discountedUnitPrice,
    standardLineValue: parseFloat(u.standardLineValue) || 0,
    waivedValue: parseFloat(u.waivedValue) || 0,
    finalLineTotal: 0,
  };
}

/** Proposal-level complimentary presentation totals (decimal-safe). */
export function computeComplimentaryPresentation(sections) {
  let standardCents = 0;
  let waivedCents = 0;
  let count = 0;
  (sections || []).forEach((section) => {
    if (!section || section.type === 'image' || !Array.isArray(section.products)) return;
    section.products.forEach((product) => {
      if (!isComplimentaryUpgradeActive(product)) return;
      const u = product.clientUpgrade;
      const std = parseFloat(u.standardLineValue) || 0;
      const waived = parseFloat(u.waivedValue) || 0;
      standardCents += Math.round(std * 100);
      waivedCents += Math.round((waived > 0 ? waived : 0) * 100);
      count += 1;
    });
  });
  return {
    complimentaryCount: count,
    hasComplimentary: count > 0,
    complimentaryStandardValue: standardCents / 100,
    complimentaryWaivedValue: waivedCents / 100,
    complimentaryDiscountAddBack: (standardCents - waivedCents) / 100,
  };
}

export function calculateDetailedTotals(proposal) {
  // PERFORMANCE: Check cache first
  const cacheKey = getCacheKey(proposal, 'detailed');
  if (calculationCache.has(cacheKey)) {
    return calculationCache.get(cacheKey);
  }
  
  // If this is a historical project, use pre-calculated values
  // Check both isHistorical flag and project number starting with HIST-
  const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
  
  let result;
  if (isHistorical && (proposal.historicalInvoiceTotal !== null && proposal.historicalInvoiceTotal !== undefined)) {
    result = {
      rentalMultiplier: 1.0,
      productSubtotal: parseFloat(proposal.historicalProductTotal || 0) || 0,
      standardRateDiscount: 0,
      rentalTotal: parseFloat(proposal.historicalProductTotal || 0) || 0,
      productCare: 0,
      serviceFee: 0,
      delivery: 0,
      discount: 0,
      subtotal: parseFloat(proposal.historicalInvoiceTotal || 0) || 0,
      tax: 0,
      total: parseFloat(proposal.historicalInvoiceTotal || 0) || 0
    };
    
    // Cache historical result
    if (calculationCache.size >= CACHE_MAX_SIZE) {
      const firstKey = calculationCache.keys().next().value;
      calculationCache.delete(firstKey);
    }
    calculationCache.set(cacheKey, result);
    return result;
  }
  
  // PERFORMANCE: Use cached parsed sections JSON
  let sections;
  const sectionsCacheKey = proposal.id || proposal.projectNumber || proposal.sectionsJSON?.substring(0, 50);
  if (sectionsCache.has(sectionsCacheKey)) {
    sections = sectionsCache.get(sectionsCacheKey);
  } else {
    sections = JSON.parse(proposal.sectionsJSON || '[]');
    sectionsCache.set(sectionsCacheKey, sections);
  }
  
  let baseProductTotal = 0;
  sections.forEach(section => {
    if (section.products && Array.isArray(section.products)) {
      section.products.forEach(product => {
        const quantity = parseFloat(product.quantity) || 0;
        const price = parseFloat(product.price) || 0;
        baseProductTotal += quantity * price;
      });
    }
  });
  
  // Get rental multiplier
  let rentalMultiplier = 1.0;
  if (proposal.customRentalMultiplier && proposal.customRentalMultiplier.trim() !== '') {
    const parsed = parseFloat(proposal.customRentalMultiplier);
    if (!isNaN(parsed) && parsed > 0) {
      rentalMultiplier = parsed;
    }
  } else {
    // Duration: delivery→strike 24-hour periods, or date-only floor (no +1) — matches Backend
    const durationInDays = getDurationInDays(proposal);
    rentalMultiplier = getRentalMultiplier(durationInDays);
  }
  
  const extendedProductTotal = baseProductTotal * rentalMultiplier;
  const productSubtotal = extendedProductTotal;
  
  // Percentage discount is on BASE product total, not extended (matches Backend)
  const discountValue = parseFloat(proposal.discountValue || proposal.discount || 0) || 0;
  let discountType = 'percentage';
  if (proposal.discountName && proposal.discountName.startsWith('TYPE:')) {
    const match = proposal.discountName.match(/^TYPE:(\w+)/);
    if (match) discountType = match[1];
  }
  const standardRateDiscount = Math.abs(discountType === 'dollar'
    ? discountValue
    : baseProductTotal * (Math.abs(discountValue) / 100));
  
  const rentalTotal = extendedProductTotal - standardRateDiscount;
  
  let waiveProductCare = proposal.waiveProductCare === true || proposal.waiveProductCare === 'true' || String(proposal.waiveProductCare || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:PC')) waiveProductCare = true;
  let waiveServiceFee = proposal.waiveServiceFee === true || proposal.waiveServiceFee === 'true' || String(proposal.waiveServiceFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:SF')) waiveServiceFee = true;

  const pcUsd = resolveProductCareDollarNumber(proposal);
  const sfUsd = resolveServiceFeeDollarNumber(proposal);
  const pcPct = resolveProductCarePercentNumber(proposal);
  const sfPct = resolveServiceFeePercentNumber(proposal);
  const pcPctEff = pcUsd !== null ? null : (pcPct !== null ? pcPct : DEFAULT_PC_PCT);
  const sfPctEff = sfUsd !== null ? null : (sfPct !== null ? sfPct : DEFAULT_SF_PCT);

  let productCareAmount;
  if (pcUsd !== null) productCareAmount = pcUsd;
  else productCareAmount = extendedProductTotal * ((pcPctEff ?? DEFAULT_PC_PCT) / 100);
  const productCare = waiveProductCare ? 0 : productCareAmount;

  const delivery = parseFloat(proposal.deliveryFee) || 0;

  let serviceFeeAmount;
  if (sfUsd !== null) serviceFeeAmount = sfUsd;
  else serviceFeeAmount = (extendedProductTotal + productCare + delivery) * ((sfPctEff ?? DEFAULT_SF_PCT) / 100);
  const serviceFee = waiveServiceFee ? 0 : serviceFeeAmount;

  const productCareFeeLabelSuffix = pcUsd !== null
    ? null
    : `(${Number(pcPctEff ?? DEFAULT_PC_PCT)}%)`;
  const serviceFeeLabelSuffix = sfUsd !== null
    ? null
    : `(${Number(sfPctEff ?? DEFAULT_SF_PCT)}%)`;
  
  // Subtotal includes all fees (matches Backend — sum all misc fee amounts)
  let miscFeesTotal = 0;
  try {
    const miscFees = typeof proposal.miscFees === 'string' ? JSON.parse(proposal.miscFees || '[]') : (proposal.miscFees || []);
    if (Array.isArray(miscFees)) {
      miscFeesTotal = miscFees.reduce((sum, fee) => sum + (parseFloat(fee?.amount || 0) || 0), 0);
    }
  } catch (_) {}
  const subtotal = rentalTotal + productCare + serviceFee + delivery + miscFeesTotal;

  const taxExempt = proposal.taxExempt === true || proposal.taxExempt === 'true' || String(proposal.taxExempt || '').toLowerCase() === 'true';

  // Whether the 3% credit card processing fee applies.
  // Use waiveCreditCardFeeFromColumn when waiveCreditCardFee not set so shareable and Reserve Portal match
  let waiveCreditCardFee = proposal.waiveCreditCardFee === true || proposal.waiveCreditCardFee === 'true' || String(proposal.waiveCreditCardFee || '').toLowerCase() === 'true';
  if (proposal.discountName && proposal.discountName.includes('WAIVE:CC')) {
    waiveCreditCardFee = true;
  }
  if (!waiveCreditCardFee && (proposal.waiveCreditCardFeeFromColumn === true || proposal.waiveCreditCardFeeFromColumn === 'true' || String(proposal.waiveCreditCardFeeFromColumn || '').toLowerCase() === 'true')) {
    waiveCreditCardFee = true;
  }

  // CC fee: 3% of (discounted subtotal + straight tax). This is the fixed, non-taxable
  // line that also appears on the QuickBooks invoice, so it is based on the straight tax.
  const straightTax = taxExempt ? 0 : subtotal * 0.0975;
  const creditCardFeeAmount = (subtotal + straightTax) * 0.03;
  const creditCardFee = waiveCreditCardFee ? 0 : creditCardFeeAmount;

  // Tax mirrors QuickBooks: the discount is prorated across all lines including the
  // non-taxable CC fee, so the slice landing on the CC fee inflates the taxable base.
  // See computeQuickBooksMirroredTax. Falls back to straight tax with no CC fee/discount.
  const tax = computeQuickBooksMirroredTax({
    subtotal,
    standardRateDiscount,
    creditCardFee,
    taxExempt,
  });
  const total = subtotal + tax + creditCardFee;

  // Complimentary-upgrade presentation add-backs (do not affect the authoritative
  // rental/subtotal/tax — complimentary lines already contribute $0).
  const complimentary = computeComplimentaryPresentation(sections);

  result = {
    rentalMultiplier,
    productSubtotal,
    standardRateDiscount,
    rentalTotal,
    ...complimentary,
    productCare,
    productCareAmount,
    serviceFee,
    serviceFeeAmount,
    delivery,
    miscFees: miscFeesTotal,
    discount: standardRateDiscount,
    subtotal,
    tax,
    creditCardFee,
    creditCardFeeAmount,
    total,
    waiveCreditCardFee,
    waiveProductCare,
    waiveServiceFee,
    productCareFlatDollars: pcUsd,
    serviceFeeFlatDollars: sfUsd,
    productCareFeeLabelSuffix,
    serviceFeeLabelSuffix
  };
  
  // PERFORMANCE: Cache result for calculateDetailedTotals
  const detailedCacheKey = getCacheKey(proposal, 'detailed');
  if (calculationCache.size >= CACHE_MAX_SIZE) {
    const firstKey = calculationCache.keys().next().value;
    calculationCache.delete(firstKey);
  }
  calculationCache.set(detailedCacheKey, result);
  
  return result;
}

export function getRentalMultiplier(duration) {
  if (duration <= 1) return 1.0;
  if (duration === 2) return 1.1;
  if (duration === 3) return 1.2;
  if (duration === 4) return 1.3;
  if (duration === 5) return 1.4;
  if (duration === 6) return 1.5;
  if (duration >= 7 && duration <= 14) return 2.0;
  if (duration >= 15 && duration <= 21) return 3.0;
  if (duration >= 22 && duration <= 28) return 4.0;
  return 4.0;
}

export function isFutureDate(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return false;
  return date > new Date();
}

export function isPastDate(dateStr) {
  const date = parseDateSafely(dateStr);
  if (!date) return false;
  return date < new Date();
}

// Calculate product spend for a proposal (used for points calculation)
// Points = Invoice Total - Tax - Delivery - Credit Card Fee
// PREFERRED: Use stored pointAllocation value from backend (Column AC for regular, Column H for historical)
// FALLBACK: Calculate only if stored value is missing
export function calculateProductSpend(proposal) {
  try {
    // PREFERRED: Use stored pointAllocation value if available (single source of truth)
    // Accept 0 as valid value (proposals might have 0 points if they're just fees)
    if (proposal.pointAllocation !== undefined && proposal.pointAllocation !== null) {
      const storedPoints = parseFloat(proposal.pointAllocation);
      if (!isNaN(storedPoints)) {
        // Use stored value even if 0 (0 is a valid point allocation)
        return storedPoints;
      }
    }
    
    // FALLBACK: Calculate if stored value is missing (backward compatibility)
    // Removed logging for performance
    
    // Check both isHistorical flag and project number starting with HIST-
    const isHistorical = proposal.isHistorical || (proposal.projectNumber && proposal.projectNumber.startsWith('HIST-'));
    
    if (isHistorical) {
      // For historical projects, use Column H point allocation if available
      // Otherwise fall back to historicalProductTotal
      if (proposal.pointAllocation !== undefined && proposal.pointAllocation !== null) {
        return parseFloat(proposal.pointAllocation) || 0;
      }
      
      if (proposal.historicalProductTotal !== null && proposal.historicalProductTotal !== undefined) {
        return parseFloat(proposal.historicalProductTotal) || 0;
      } else {
        return 0;
      }
    }
    
    // For regular proposals, calculate using backend formula
    // Points = Invoice Total - Tax - Delivery - Credit Card Fee
    const totals = calculateDetailedTotals(proposal);
    
    const invoiceTotal = totals.total; // Full invoice total (subtotal + tax + credit card fee)
    const tax = totals.tax || 0;
    const delivery = totals.delivery || 0;
    const creditCardFee = totals.creditCardFee || 0;
    
    // Backend formula: Points = Invoice Total - Tax - Delivery - Credit Card Fee
    const points = invoiceTotal - tax - delivery - creditCardFee;
    
    // Debug logging to verify calculation
    console.log(`[Points Calculation] Project ${proposal.projectNumber || 'N/A'}:`, {
      invoiceTotal: invoiceTotal.toFixed(2),
      tax: tax.toFixed(2),
      delivery: delivery.toFixed(2),
      creditCardFee: creditCardFee.toFixed(2),
      points: points.toFixed(2),
      subtotal: totals.subtotal?.toFixed(2),
      rentalTotal: totals.rentalTotal?.toFixed(2),
      productCare: totals.productCare?.toFixed(2),
      serviceFee: totals.serviceFee?.toFixed(2),
      calculation: `(${invoiceTotal.toFixed(2)} - ${tax.toFixed(2)} - ${delivery.toFixed(2)} - ${creditCardFee.toFixed(2)}) = ${points.toFixed(2)}`
    });
    
    return Math.max(0, points); // Ensure non-negative
  } catch (e) {
    console.error('Error calculating product spend:', e);
    return 0;
  }
}
